import { useState } from 'react';
import { Modal, ModalContent, ModalHeader, ModalBody, Button } from '@heroui/react';
import { Keyboard, List } from '@phosphor-icons/react';
import { useKeyboardShortcuts } from 'shared/contexts/keyboardShortcuts/keyboardShortcuts.context';

interface KeyboardShortcutsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Static shortcuts list as fallback
const allShortcuts = [
  {
    key: 'shift+s',
    description: 'Focus globale zoekfunctie',
    scope: 'global' as const
  },
  {
    key: 'shift+f', 
    description: 'Focus pagina zoeken (FilterBar)',
    scope: 'page' as const
  },
  {
    key: 'shift+g',
    description: 'Open filter paneel',
    scope: 'page' as const
  }
];

export const KeyboardShortcutsModal: React.FC<KeyboardShortcutsModalProps> = ({
  isOpen,
  onClose
}) => {
  const { getActiveShortcuts } = useKeyboardShortcuts();
  const [showAll, setShowAll] = useState(false);

  const activeShortcuts = getActiveShortcuts();
  
  // Use active shortcuts if available, otherwise fall back to static list
  const displayShortcuts = showAll 
    ? (activeShortcuts.length > 0 ? [...activeShortcuts, ...allShortcuts.filter(s => !activeShortcuts.some(a => a.key === s.key))] : allShortcuts)
    : (activeShortcuts.length > 0 ? activeShortcuts : allShortcuts.filter(s => s.scope === 'global'));

  const currentPageShortcuts = displayShortcuts.filter(s => s.scope === 'page').length;
  const globalShortcuts = displayShortcuts.filter(s => s.scope === 'global').length;

  return (
    <Modal 
      isOpen={isOpen} 
      onOpenChange={onClose}
      size="lg"
    >
      <ModalContent>
        <ModalHeader className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Keyboard size={20} className="text-moss-primary" />
            <span className="text-lg font-semibold">Sneltoetsen</span>
          </div>
          <Button
            size="sm"
            variant="light"
            onPress={() => setShowAll(!showAll)}
            className="text-xs"
            startContent={<List size={14} />}
          >
            {showAll ? 'Deze pagina' : 'Alle shortcuts'}
          </Button>
        </ModalHeader>

        <ModalBody>
          {/* Status indicator */}
          <div className="text-xs text-neutral-secondary-mid mb-3">
            {showAll 
              ? `Alle shortcuts (${displayShortcuts.length})`
              : `Deze pagina (${globalShortcuts} globaal, ${currentPageShortcuts} pagina-specifiek)`
            }
          </div>

          <div className="space-y-3">
            {displayShortcuts.map((shortcut, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
                <div className="flex-1">
                  <div className="font-medium text-neutral-primary text-sm">
                    {shortcut.description}
                  </div>
                  <div className="text-xs text-neutral-secondary-mid mt-1">
                    {shortcut.scope === 'global' ? 'Werkt overal' : 'Pagina-specifiek'}
                  </div>
                </div>
                <div className="flex items-center gap-1 px-3 py-1.5 bg-white rounded-lg border font-mono text-sm">
                  {shortcut.key.split('+').map((key, i) => (
                    <span key={i} className="flex items-center">
                      {i > 0 && <span className="text-neutral-400 mx-1">+</span>}
                      <span className="bg-neutral-100 px-2 py-1 rounded-sm text-xs font-semibold">
                        {key.toUpperCase()}
                      </span>
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {displayShortcuts.length === 0 && (
            <div className="text-center py-8 text-neutral-secondary-mid">
              <Keyboard size={48} className="mx-auto mb-3 opacity-30" />
              <p className="text-sm">Geen shortcuts beschikbaar voor deze pagina.</p>
            </div>
          )}

          <div className="mt-4 p-3 bg-moss-primary/5 rounded-lg">
            <p className="text-xs text-neutral-600">
              💡 <strong>Tip:</strong> Shortcuts werken niet wanneer je typt in tekstvelden.
            </p>
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
